import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Sparkles,
  ChevronDown,
  CheckCircle2,
  CircleDot,
  Terminal,
} from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_prompt-mastery-64/artifacts/3omzwe9h_1773800644378.png";

/* -------------------- Shared bits -------------------- */
export const Container = ({ className = "", children }) => (
  <div className={`max-w-7xl mx-auto px-6 sm:px-8 ${className}`}>{children}</div>
);

export const Overline = ({ children }) => (
  <span className="font-mono text-[11px] sm:text-xs font-bold uppercase tracking-[0.28em] text-cyan-400">
    {children}
  </span>
);

export const Reveal = ({ children, delay = 0, y = 24, className = "" }) => {
  const prefersReduced = useReducedMotion();
  if (prefersReduced) return <div className={className}>{children}</div>;
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export const CourseHeader = ({ enrollUrl, enrollLabel = "Enroll" }) => (
  <header className="sticky top-0 z-50 backdrop-blur-xl bg-slate-950/70 border-b border-white/10">
    <Container className="flex items-center justify-between h-16 sm:h-20">
      <Link to="/" data-testid="course-nav-logo" className="flex items-center gap-3">
        <img src={LOGO_URL} alt="JK Academy" className="h-9 w-9 sm:h-10 sm:w-10 object-contain" />
        <div className="leading-tight">
          <div className="font-display font-extrabold tracking-tight text-white text-base sm:text-lg">
            JK ACADEMY
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-400 hidden sm:block">
            Build AI Systems. Not Just Prompts.
          </div>
        </div>
      </Link>
      <nav className="hidden md:flex items-center gap-7 font-mono text-xs uppercase tracking-[0.2em] text-slate-400">
        <Link to="/" className="hover:text-cyan-300 transition-colors">
          Courses
        </Link>
        <Link to="/services" className="hover:text-cyan-300 transition-colors">
          Services
        </Link>
      </nav>
      <a
        href={enrollUrl}
        rel="noopener noreferrer"
        data-testid="course-nav-enroll"
        className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold font-mono uppercase tracking-[0.18em] text-[10px] sm:text-[11px] px-3 sm:px-4 py-2 sm:py-2.5 shadow-[0_0_20px_rgba(6,182,212,0.35)] hover:shadow-[0_0_36px_rgba(6,182,212,0.6)] transition-all"
      >
        {enrollLabel}
        <ArrowRight className="h-3.5 w-3.5" />
      </a>
    </Container>
  </header>
);

export const CourseFooter = () => (
  <footer className="border-t border-white/10 bg-slate-950/60 py-12 pb-24 sm:pb-14">
    <Container className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
      <div className="flex items-center gap-3">
        <img src={LOGO_URL} alt="JK Academy" className="h-8 w-8 object-contain" />
        <div className="leading-tight">
          <div className="font-display font-extrabold text-white text-sm">JK ACADEMY</div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
            Build AI Systems. Not Just Prompts.
          </div>
        </div>
      </div>
      <nav className="flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400">
        <Link to="/" className="hover:text-cyan-300 transition-colors">Home</Link>
        <Link to="/services" className="hover:text-cyan-300 transition-colors">Services</Link>
        <Link to="/copilot" className="hover:text-cyan-300 transition-colors">Copilot</Link>
        <Link to="/executive-workflow" className="hover:text-cyan-300 transition-colors">Executive Workflow</Link>
      </nav>
      <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
        © {new Date().getFullYear()} JK Academy
      </p>
    </Container>
  </footer>
);

export const CardShell = ({ children, className = "" }) => (
  <div
    className={`relative border border-white/10 bg-slate-900/50 backdrop-blur-md p-7 sm:p-8 hover:border-cyan-500/60 hover:bg-slate-900/70 transition-colors ${className}`}
  >
    {children}
  </div>
);

export const BulletList = ({ items, testIdPrefix }) => (
  <ul className="space-y-2.5">
    {items.map((t, i) => (
      <li
        key={t}
        data-testid={testIdPrefix ? `${testIdPrefix}-${i}` : undefined}
        className="flex items-start gap-3 text-slate-200 text-sm sm:text-base"
      >
        <CheckCircle2 className="h-4 w-4 text-cyan-400 shrink-0 mt-1" />
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

export const FaqItem = ({ item, i, open, onToggle, testIdPrefix }) => {
  const panelId = `${testIdPrefix}-panel-${i}`;
  return (
    <div
      data-testid={`${testIdPrefix}-${i}`}
      className="border-b border-white/10 last:border-0"
    >
      <button
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={panelId}
        data-testid={`${testIdPrefix}-${i}-toggle`}
        className="w-full flex items-center justify-between text-left py-5 gap-6 group"
      >
        <span className="font-display text-base sm:text-lg font-semibold text-white group-hover:text-cyan-300 transition-colors">
          {item.q}
        </span>
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="shrink-0 h-8 w-8 flex items-center justify-center border border-cyan-500/40 bg-slate-950 text-cyan-300"
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </motion.span>
      </button>
      <motion.div
        id={panelId}
        role="region"
        aria-hidden={!open}
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="overflow-hidden"
      >
        <p className="pb-5 pr-12 text-slate-300 font-mono text-sm leading-relaxed">
          {item.a}
        </p>
      </motion.div>
    </div>
  );
};

export const Faq = ({ items, testIdPrefix = "course-faq" }) => {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <div className="max-w-3xl border-t border-white/10" data-testid={`${testIdPrefix}-list`}>
      {items.map((item, i) => (
        <FaqItem
          key={item.q}
          item={item}
          i={i}
          open={openIdx === i}
          onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
          testIdPrefix={testIdPrefix}
        />
      ))}
    </div>
  );
};

/**
 * Set SEO tags (title + meta description) and reset when the page unmounts.
 */
export const useSeo = ({ title, description }) => {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      let m = document.querySelector('meta[name="description"]');
      if (!m) {
        m = document.createElement("meta");
        m.setAttribute("name", "description");
        document.head.appendChild(m);
      }
      m.setAttribute("content", description);
    }
    return () => {
      document.title = "JK Academy — Build AI Systems. Not Just Prompts.";
    };
  }, [title, description]);
};

/* Named re-export so pages can pull icons and motion from one place */
export { motion, ArrowRight, ArrowUpRight, Sparkles, CircleDot, Terminal };
